import { PantallaCargando } from '@/components/Cargando';
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api, { formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { FileText, Plus, Send, RefreshCw, Archive, Copy, HelpCircle, Eye, Sparkles, AlertTriangle, Link2, CheckCircle2 } from 'lucide-react';

const CAT_OPTS = [{ v: 'utility', l: 'Utilidad' }, { v: 'marketing', l: 'Promoción' }, { v: 'authentication', l: 'Verificación' }];
const STATUS_COLORS = { 'Borrador': 'bg-gray-100 text-gray-600', 'Enviada a Meta': 'bg-yellow-100 text-yellow-700', 'En revisión': 'bg-yellow-100 text-yellow-700', 'En revisión del proveedor': 'bg-yellow-100 text-yellow-700', 'Pendiente en Gupshup Partner': 'bg-amber-100 text-amber-800', 'Aprobada': 'bg-emerald-100 text-emerald-700', 'Rechazada': 'bg-red-100 text-red-600', 'Archivada': 'bg-gray-50 text-gray-400' };
const VARS = [{ key: 'nombre_cliente', desc: 'Nombre del cliente' }, { key: 'nombre_negocio', desc: 'Tu negocio' }, { key: 'fecha_cita', desc: 'Fecha de la cita' }, { key: 'telefono', desc: 'Teléfono del cliente' }];

export default function MarketingTemplates() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [showEditor, setShowEditor] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showHelp, setShowHelp] = useState(false);
  const [previewTpl, setPreviewTpl] = useState(null);
  const [waStatus, setWaStatus] = useState(null); // full connectivity payload
  const [syncing, setSyncing] = useState(false);
  const readyMetaTpl = Boolean(waStatus?.ready_for_meta_templates);
  const readySend = Boolean(waStatus?.ready_for_send);
  const hasOtherSender = readySend && !readyMetaTpl;
  const [kitOpen, setKitOpen] = useState(false);
  const [kit, setKit] = useState(null);
  const [kitLoading, setKitLoading] = useState(false);
  const [submittingAll, setSubmittingAll] = useState(false);

  const pendingPartner = templates.filter(
    (t) => t.external_status === 'PENDING_PARTNER' || t.status_label === 'Pendiente en Gupshup Partner'
  );

  const submitAllDrafts = async () => {
    setSubmittingAll(true);
    try {
      const { data } = await api.post('/tenant/marketing/templates/submit-all-drafts');
      toast.success(
        data.submitted
          ? `${data.submitted} plantilla(s) enviadas / en cola. Fallidas: ${data.failed || 0}`
          : (data.message || 'Proceso completado')
      );
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || 'No se pudo enviar a revisión');
    } finally {
      setSubmittingAll(false);
    }
  };

  const openPartnerKit = async () => {
    setKitLoading(true);
    setKitOpen(true);
    try {
      const { data } = await api.get('/tenant/marketing/templates/partner-kit');
      setKit(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || 'No se pudo cargar el kit');
      setKitOpen(false);
    } finally {
      setKitLoading(false);
    }
  };

  const markPartnerApproved = async (id) => {
    try {
      const { data } = await api.post(`/tenant/marketing/templates/${id}/mark-partner-approved`);
      toast.success(data.message || 'Marcada como aprobada');
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const copyText = async (text, label = 'Copiado') => {
    try {
      await navigator.clipboard.writeText(text || '');
      toast.success(label);
    } catch {
      toast.message(text);
    }
  };


  const load = () => {
    setLoading(true);
    Promise.all([
      api.get('/tenant/marketing/templates').catch(() => ({ data: [] })),
      api.get('/tenant/whatsapp/business/status').catch(() => ({ data: { connected: false } })),
    ]).then(([tRes, sRes]) => {
      setTemplates(tRes.data || []);
      setWaStatus(sRes.data || {});
    }).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const syncFromMeta = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post('/tenant/whatsapp/business/templates/sync');
      toast.success(data.message || 'Plantillas sincronizadas con Meta');
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || 'No se pudo sincronizar con Meta');
    } finally {
      setSyncing(false);
    }
  };

  const hasTwilio = Boolean(
    (waStatus?.senders || []).some(s => s.provider_key === 'twilio_whatsapp' && s.connected)
    || waStatus?.platform_infrastructure?.twilio_whatsapp_pool
  );

  const syncTwilioContent = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post('/tenant/whatsapp/twilio/templates/sync');
      toast.success(data.message || 'Plantillas Content de Twilio sincronizadas');
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || 'No se pudo sincronizar Twilio Content');
    } finally {
      setSyncing(false);
    }
  };

  const filtered = filter === 'all' ? templates : templates.filter(t => {
    if (filter === 'draft') return t.status === 'draft';
    if (filter === 'pending') return t.status === 'pending_approval';
    if (filter === 'approved') return t.external_status === 'APPROVED';
    return true;
  });

  const submitMeta = async (id) => {
    try {
      // S102: prefer canal WhatsApp Business; fallback marketing alias
      let data;
      try {
        ({ data } = await api.post(`/tenant/whatsapp/business/templates/${id}/submit`));
      } catch (e1) {
        ({ data } = await api.post(`/tenant/marketing/templates/${id}/submit-meta`));
      }
      toast.success(data.message || 'Enviada a Meta');
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const refreshMeta = async (id) => {
    try {
      await api.post(`/tenant/whatsapp/business/templates/${id}/refresh`).catch(
        () => api.post(`/tenant/marketing/templates/${id}/refresh-status`)
      );
      toast.success('Estado actualizado desde Meta');
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };


  const archive = async (id) => {
    try { await api.delete(`/tenant/marketing/templates/${id}`); toast.success('Plantilla archivada'); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const duplicate = (t) => {
    setEditing({ ...t, id: null, name: `${t.name} (copia)`, status: 'draft' });
    setShowEditor(true);
  };

  if (loading) return <PantallaCargando />;

  return (
    <div className="space-y-4" data-testid="marketing-templates">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <h2 className="text-base font-semibold text-[#0F172A]">Plantillas WhatsApp Business</h2>
          <Button variant="ghost" size="sm" onClick={() => setShowHelp(true)} data-testid="marketing-help-btn"><HelpCircle className="w-4 h-4 text-[#94A3B8]" /></Button>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={submitAllDrafts} disabled={submittingAll}
            data-testid="wa-templates-submit-all" title="Envía borradores a revisión del proveedor del sender">
            <Send className={`w-3.5 h-3.5 mr-1 ${submittingAll ? 'animate-pulse' : ''}`} />
            {submittingAll ? 'Enviando…' : 'Enviar todas a revisión'}
          </Button>
          <Button variant="outline" size="sm" onClick={openPartnerKit}
            data-testid="wa-templates-partner-kit" title="Guía y textos listos para Gupshup Partner">
            <Sparkles className="w-3.5 h-3.5 mr-1" />
            Guía Gupshup Partner
          </Button>
          <Button variant="outline" size="sm" onClick={syncFromMeta} disabled={syncing || !readyMetaTpl}
            data-testid="wa-templates-sync" title="Trae las plantillas ya creadas en tu cuenta Meta">
            <RefreshCw className={`w-3.5 h-3.5 mr-1 ${syncing ? 'animate-spin' : ''}`} />
            {syncing ? 'Sincronizando…' : 'Sincronizar con Meta'}
          </Button>
          {hasTwilio && (
            <Button variant="outline" size="sm" onClick={syncTwilioContent} disabled={syncing}
              data-testid="wa-templates-sync-twilio" title="Trae Content Templates de Twilio (HX…)">
              <RefreshCw className={`w-3.5 h-3.5 mr-1 ${syncing ? 'animate-spin' : ''}`} />
              Sincronizar Twilio Content
            </Button>
          )}
          <Button onClick={() => { setEditing(null); setShowEditor(true); }} className="bg-[#0F172A] text-white" data-testid="template-create-btn">
            <Plus className="w-4 h-4 mr-1" /> Crear plantilla
          </Button>
        </div>
      </div>


      {pendingPartner.length > 0 && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950 space-y-2" data-testid="wa-pending-partner-banner">
          <p className="font-semibold flex items-center gap-1">
            <AlertTriangle className="w-4 h-4 text-amber-600" />
            {pendingPartner.length} plantilla(s) listas para crear en Gupshup Partner
          </p>
          <p className="text-xs text-amber-900/90">
            Tu API key de Gupshup envía mensajes, pero las plantillas se crean en el panel Partner.
            Usa la guía: copia el <strong>element name</strong> y el cuerpo, créalas en Partner y luego marca «Aprobada en Partner».
          </p>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" className="bg-amber-800 hover:bg-amber-900 text-white h-8" onClick={openPartnerKit}>
              Abrir guía paso a paso
            </Button>
          </div>
        </div>
      )}

      {readyMetaTpl && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 px-4 py-2.5 flex flex-wrap items-center gap-2 text-sm text-emerald-900" data-testid="wa-templates-connected">
          <CheckCircle2 className="w-4 h-4 shrink-0" />
          <span>
            Meta Cloud listo. Puedes <strong>Sincronizar</strong> o <strong>Enviar a Meta</strong> (revisión).
            {waStatus?.guidance?.templates_review ? ` ${waStatus.guidance.templates_review}` : ''}
          </span>
        </div>
      )}
      {hasOtherSender && (
        <div className="rounded-xl border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-950 space-y-1" data-testid="wa-templates-other-provider">
          <p className="font-semibold flex items-center gap-1"><CheckCircle2 className="w-4 h-4 text-sky-600" /> Tienes canal WhatsApp (Gupshup/Twilio)</p>
          <p className="text-xs text-sky-900/80">
            <strong>No necesitas Meta</strong> para enviar. Las plantillas de este listado son las de <em>Cloud API / Meta</em>.
            Con Gupshup o Twilio las plantillas se aprueban en su panel, no con el botón «Enviar a Meta».
          </p>
          <Link to="/tenant/whatsapp/conexion" className="inline-flex text-xs font-semibold text-sky-800 underline">
            Ver canales conectados →
          </Link>
        </div>
      )}
      {!readySend && !readyMetaTpl && waStatus && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 flex flex-wrap items-center justify-between gap-2" data-testid="wa-templates-need-connect">
          <div className="flex items-start gap-2 text-sm text-amber-900">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            <p>
              Para <strong>revisión de plantillas Meta</strong>: conecta Meta en un clic (ventana de Facebook).
              Si solo quieres enviar con Gupshup/Twilio, conéctalos en Integraciones — no hace falta Meta.
            </p>
          </div>
          <Link to="/tenant/whatsapp/conexion"
            className="inline-flex items-center gap-1 text-xs font-semibold rounded-lg bg-[#0F172A] text-white px-3 py-2">
            <Link2 className="w-3.5 h-3.5" /> Cómo conectar
          </Link>
        </div>
      )}
      <div className="flex gap-1.5">
        {[['all', 'Todas'], ['draft', 'Borradores'], ['pending', 'Pendientes'], ['approved', 'Aprobadas']].map(([k, l]) => (
          <button key={k} onClick={() => setFilter(k)} className={`px-3 py-1 rounded-full text-xs font-medium ${filter === k ? 'bg-[#0F172A] text-white' : 'bg-[#F1F5F9] text-[#64748B]'}`}>{l}</button>
        ))}
      </div>
      {filtered.length === 0 ? (
        <div className="text-center py-12 bg-white border border-[#E2E8F0] rounded-xl">
          <FileText className="w-10 h-10 text-[#CBD5E1] mx-auto mb-3" />
          <p className="text-sm text-[#64748B]">Crea tu primera plantilla para enviar mensajes por WhatsApp</p>
        </div>
      ) : (
        <div className="bg-white border border-[#E2E8F0] rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead><tr className="bg-[#F8FAFC] border-b border-[#E2E8F0]">
              <th className="text-left px-4 py-2.5 text-xs font-semibold text-[#64748B]">Nombre</th>
              <th className="text-left px-4 py-2.5 text-xs font-semibold text-[#64748B]">Categoría</th>
              <th className="text-left px-4 py-2.5 text-xs font-semibold text-[#64748B]">Estado</th>
              <th className="w-48" />
            </tr></thead>
            <tbody>
              {filtered.map(t => (
                <tr key={t.id} className="border-b border-[#F1F5F9]">
                  <td className="px-4 py-3">
                    <span className="font-medium text-[#0F172A]">{t.name}</span>
                    {t.is_system && <span className="ml-2 px-1.5 py-0.5 text-[9px] bg-blue-50 text-blue-600 rounded border border-blue-200">Sistema</span>}
                    {(t.provider_key === 'twilio_whatsapp' || (t.external_template_id || '').startsWith('HX')) && (
                      <span className="ml-2 px-1.5 py-0.5 text-[9px] bg-red-50 text-[#B91C1C] rounded border border-red-200">Twilio Content</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-[#64748B]">{t.category_label}</td>
                  <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${STATUS_COLORS[t.status_label] || 'bg-gray-100 text-gray-600'}`}>{t.status_label}</span></td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1 justify-end">
                      <Button variant="ghost" size="sm" onClick={() => setPreviewTpl(t)} title="Vista previa"><Eye className="w-3.5 h-3.5" /></Button>
                      {t.status === 'draft' && <Button variant="ghost" size="sm" onClick={() => submitMeta(t.id)} data-testid="template-submit-meta-btn" title="Enviar a revisión Meta" disabled={!readyMetaTpl}><Send className="w-3.5 h-3.5 text-[#128C7E]" /></Button>}
                      {(t.status === 'pending_approval' || t.external_status === 'PENDING' || t.external_status === 'IN_REVIEW') && (
                        <Button variant="ghost" size="sm" onClick={() => refreshMeta(t.id)} title="Consultar estado en Meta"><RefreshCw className="w-3.5 h-3.5" /></Button>
                      )}
                      <Button variant="ghost" size="sm" onClick={() => duplicate(t)} title="Duplicar"><Copy className="w-3.5 h-3.5" /></Button>
                      {!t.is_system && t.status !== 'disabled' && <Button variant="ghost" size="sm" onClick={() => archive(t.id)} title="Archivar"><Archive className="w-3.5 h-3.5 text-[#94A3B8]" /></Button>}
                      {(t.status === 'draft' || !t.external_status) && <Button variant="ghost" size="sm" onClick={() => { setEditing(t); setShowEditor(true); }}>Editar</Button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {showEditor && <TemplateEditor initial={editing} onClose={() => setShowEditor(false)} onSaved={load} />}
      {previewTpl && <TemplatePreview tpl={previewTpl} onClose={() => setPreviewTpl(null)} />}

      <Dialog open={kitOpen} onOpenChange={setKitOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="partner-kit-dialog">
          <DialogHeader>
            <DialogTitle>Guía Gupshup Partner — plantillas</DialogTitle>
          </DialogHeader>
          {kitLoading && <p className="text-sm text-[#64748B]">Cargando kit…</p>}
          {kit && (
            <div className="space-y-4 text-sm text-[#334155]">
              <ol className="list-decimal pl-5 space-y-1 text-xs text-[#64748B]">
                {(kit.steps || []).map((s, i) => <li key={i}>{s}</li>)}
              </ol>
              <p className="text-xs font-medium text-amber-800 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
                Pendientes: {kit.pending_count ?? 0} · App: {kit.app_hint || 'cavernicolApp'}
              </p>
              <div className="space-y-3">
                {(kit.pending || kit.items || []).filter(i => i.needs_partner_action !== false).map((item) => (
                  <div key={item.id} className="border border-[#E2E8F0] rounded-xl p-3 space-y-2 bg-[#F8FAFC]">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <p className="font-semibold text-[#0F172A] text-sm">{item.name}</p>
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">{item.status_label}</span>
                    </div>
                    <div className="flex flex-wrap gap-2 items-center">
                      <code className="text-[11px] bg-white border px-2 py-1 rounded">{item.gupshup_element_name}</code>
                      <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => copyText(item.gupshup_element_name, 'Element name copiado')}>Copiar name</Button>
                      <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => copyText(item.gupshup_body, 'Cuerpo copiado')}>Copiar cuerpo</Button>
                      <Button size="sm" className="h-7 text-xs bg-emerald-700 hover:bg-emerald-800 text-white" onClick={() => markPartnerApproved(item.id)}>Ya está aprobada</Button>
                    </div>
                    <pre className="text-[11px] whitespace-pre-wrap bg-white border rounded-lg p-2 text-[#475569]">{item.gupshup_body}</pre>
                    {item.gupshup_param_map?.length > 0 && (
                      <p className="text-[10px] text-[#94A3B8]">
                        Variables: {item.gupshup_param_map.map(p => `{{${p.index}}}=${p.key}`).join(' · ')}
                      </p>
                    )}
                    <p className="text-[10px] text-[#94A3B8]">Categoría: {item.category_gupshup} · Idioma: {item.language_gupshup}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={showHelp} onOpenChange={setShowHelp}>
        <DialogContent><DialogHeader><DialogTitle>¿Qué son las plantillas?</DialogTitle></DialogHeader>
          <div className="text-sm text-[#64748B] space-y-2">
            <p><strong>Enviar a revisión Meta</strong> solo aplica si conectaste <em>Meta Cloud</em> (botón de un clic en Conexión). No copies tokens.</p>
            <p>Si usas <strong>Gupshup o Twilio</strong>, no necesitas Meta: usa <em>Guía Gupshup Partner</em> o sincroniza Twilio Content.</p>
            <p><strong>TimeUp vendedor:</strong> con ventana 24h abierta se avisa en texto libre; si se cierra, se usa plantilla aprobada de aviso al vendedor.</p>
            <p><strong>Sincronizar con Meta</strong> trae las del WABA. El avión manda un borrador a revisión (hasta 24 h).</p>
            <p>Variables: {"{{nombre_cliente}}"}. Solo las aprobadas por Meta sirven en envíos Cloud API.</p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function TemplateEditor({ initial, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: initial?.name || '', category: initial?.category || 'utility', language: 'es',
    header_type: initial?.header_type || null, header_text: initial?.header_text || '',
    body_text: initial?.body_text || '', footer_text: initial?.footer_text || '',
    buttons: initial?.buttons || [], variables: initial?.variables || [],
  });
  const [saving, setSaving] = useState(false);
  const [assistLoading, setAssistLoading] = useState(false);
  const [assistPrompt, setAssistPrompt] = useState('');
  const [suggestions, setSuggestions] = useState(null);

  const insertVar = (key) => {
    setForm(f => ({ ...f, body_text: f.body_text + `{{${key}}}`, variables: [...f.variables.filter(v => v.key !== key), { key, sample_value: key }] }));
  };

  const runAssist = async (mode) => {
    setAssistLoading(true);
    try {
      const { data } = await api.post('/tenant/marketing/templates/assist', {
        mode,
        name: form.name,
        category: form.category,
        body_text: form.body_text,
        header_text: form.header_text,
        footer_text: form.footer_text,
        user_prompt: assistPrompt || undefined,
      });
      setSuggestions(data.suggestions || null);
      toast.success('Asistente listo — revisa las sugerencias');
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setAssistLoading(false);
  };

  const applySuggestions = () => {
    if (!suggestions) return;
    const vars = [];
    const body = suggestions.suggested_body_text || form.body_text;
    const varMatches = [...(body || '').matchAll(/\{\{([^}]+)\}\}/g)];
    varMatches.forEach(m => {
      const key = m[1];
      if (key && !vars.find(v => v.key === key)) vars.push({ key, sample_value: key });
    });
    setForm(f => ({
      ...f,
      name: suggestions.suggested_name || f.name,
      category: suggestions.suggested_category || f.category,
      header_text: suggestions.suggested_header_text ?? f.header_text,
      body_text: suggestions.suggested_body_text || suggestions.utility_rewrite || f.body_text,
      footer_text: suggestions.suggested_footer_text ?? f.footer_text,
      header_type: (suggestions.suggested_header_text || f.header_text) ? 'text' : f.header_type,
      variables: vars.length ? vars : f.variables,
    }));
    toast.success('Sugerencias aplicadas al borrador');
  };

  const save = async () => {
    setSaving(true);
    try {
      if (initial?.id) await api.put(`/tenant/marketing/templates/${initial.id}`, form);
      else await api.post('/tenant/marketing/templates', form);
      toast.success('Plantilla guardada'); onSaved(); onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setSaving(false);
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="template-editor">
        <DialogHeader><DialogTitle>{initial?.id ? 'Editar plantilla' : 'Crear plantilla'}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div><label className="text-xs font-semibold text-[#64748B]">Nombre interno</label>
            <input value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} className="w-full mt-1 px-3 py-2 text-sm border border-[#E2E8F0] rounded-lg" /></div>
          <div><label className="text-xs font-semibold text-[#64748B]">Categoría</label>
            <select value={form.category} onChange={e => setForm(f => ({...f, category: e.target.value}))} className="w-full mt-1 px-3 py-2 text-sm border border-[#E2E8F0] rounded-lg">
              {CAT_OPTS.map(c => <option key={c.v} value={c.v}>{c.l}</option>)}</select></div>
          <div><label className="text-xs font-semibold text-[#64748B]">Encabezado (opcional)</label>
            <select value={form.header_type || ''} onChange={e => setForm(f => ({...f, header_type: e.target.value || null}))} className="w-full mt-1 px-3 py-2 text-sm border border-[#E2E8F0] rounded-lg">
              <option value="">Ninguno</option><option value="text">Texto</option><option value="image">Imagen</option></select>
            {form.header_type === 'text' && <input value={form.header_text} onChange={e => setForm(f => ({...f, header_text: e.target.value}))} className="w-full mt-1 px-3 py-2 text-sm border border-[#E2E8F0] rounded-lg" placeholder="Texto del encabezado" />}</div>
          <div><label className="text-xs font-semibold text-[#64748B]">Cuerpo del mensaje</label>
            <textarea value={form.body_text} onChange={e => setForm(f => ({...f, body_text: e.target.value}))} rows={4}
              className="w-full mt-1 px-3 py-2 text-sm border border-[#E2E8F0] rounded-lg" data-testid="template-editor-body" />
            <div className="flex gap-1.5 mt-1 flex-wrap">
              {VARS.map(v => <button key={v.key} onClick={() => insertVar(v.key)} title={v.desc}
                className="px-2 py-0.5 text-[10px] bg-violet-50 text-violet-700 rounded border border-violet-200 hover:bg-violet-100">{`{{${v.key}}}`}</button>)}</div></div>
          <div><label className="text-xs font-semibold text-[#64748B]">Pie (opcional)</label>
            <input value={form.footer_text} onChange={e => setForm(f => ({...f, footer_text: e.target.value}))} className="w-full mt-1 px-3 py-2 text-sm border border-[#E2E8F0] rounded-lg" /></div>

          <div className="border border-violet-200 bg-violet-50/50 rounded-lg p-3 space-y-2" data-testid="template-assistant-panel">
            <div className="flex items-center gap-2 text-sm font-semibold text-violet-900">
              <Sparkles className="w-4 h-4" /> Asistente de plantillas Meta
            </div>
            <p className="text-[11px] text-violet-800/80">
              Te ayuda a elegir <strong>Utilidad</strong> vs <strong>Promoción</strong> y evitar que Meta reclasifique tu plantilla (más costo).
            </p>
            <input
              value={assistPrompt}
              onChange={e => setAssistPrompt(e.target.value)}
              placeholder="Ej: recordatorio de cita manicura mañana"
              className="w-full px-3 py-2 text-sm border border-violet-200 rounded-lg bg-white"
            />
            <div className="flex flex-wrap gap-2">
              <Button type="button" variant="outline" size="sm" disabled={assistLoading} onClick={() => runAssist('review')}>
                {assistLoading ? 'Analizando...' : 'Revisar borrador'}
              </Button>
              <Button type="button" variant="outline" size="sm" disabled={assistLoading} onClick={() => runAssist('create')}>
                Crear con IA
              </Button>
              <Button type="button" variant="outline" size="sm" disabled={assistLoading} onClick={() => runAssist('fix_category')}>
                Corregir a Utilidad
              </Button>
            </div>
            {suggestions && (
              <div className="bg-white border border-violet-100 rounded-lg p-3 text-xs space-y-2">
                <p><strong>Categoría sugerida:</strong> {suggestions.suggested_category} ({suggestions.category_confidence})</p>
                <p className="text-[#64748B]">{suggestions.category_reason}</p>
                {suggestions.meta_reclassify_risk === 'high' && (
                  <p className="text-amber-700 flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> Riesgo alto de reclasificación a Promoción</p>
                )}
                {(suggestions.warnings || []).map((w, i) => <p key={i} className="text-amber-700">• {w}</p>)}
                {(suggestions.tips || []).slice(0, 3).map((t, i) => <p key={i} className="text-[#64748B]">→ {t}</p>)}
                <Button type="button" size="sm" className="bg-violet-700 text-white hover:bg-violet-800" onClick={applySuggestions}>
                  Aplicar sugerencias
                </Button>
              </div>
            )}
          </div>

          {/* Preview */}
          <div className="bg-[#E5DDD5] rounded-lg p-4" data-testid="template-preview">
            <div className="bg-white rounded-lg p-3 max-w-[280px] shadow-sm">
              {form.header_text && <p className="text-sm font-semibold text-[#0F172A] mb-1">{form.header_text}</p>}
              <p className="text-sm text-[#0F172A] whitespace-pre-wrap">{form.body_text || 'Escribe tu mensaje...'}</p>
              {form.footer_text && <p className="text-[10px] text-[#94A3B8] mt-1">{form.footer_text}</p>}
            </div>
          </div>
          <Button onClick={save} disabled={saving || !form.name || !form.body_text} className="w-full bg-[#0F172A] text-white">{saving ? 'Guardando...' : 'Guardar plantilla'}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function TemplatePreview({ tpl, onClose }) {
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-sm" data-testid="template-preview-modal">
        <DialogHeader><DialogTitle>Vista previa: {tpl.name}</DialogTitle></DialogHeader>
        <div className="bg-[#E5DDD5] rounded-lg p-4">
          <div className="bg-white rounded-lg p-3 max-w-[280px] shadow-sm mx-auto">
            {tpl.header_text && <p className="text-sm font-semibold text-[#0F172A] mb-1">{tpl.header_text}</p>}
            <p className="text-sm text-[#0F172A] whitespace-pre-wrap">{tpl.body_text}</p>
            {tpl.footer_text && <p className="text-[10px] text-[#94A3B8] mt-1">{tpl.footer_text}</p>}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
